import {
  ClearingCreatedActivity,
  EventSaleChangeActivity,
  InviteUserActivity,
  MembershipChangedEvent,
  UserAddedToCompanyActivity,
  UserRemovedFromCompanyActivity,
} from '../model/activity';
import { Membership } from '../model/membership';
import { userNameAndOrEmail } from '../model/user';
import { pluralize, formatDate } from '../util/util';
import { I18n } from './translate';

export default {
  actions: {
    add: (item: string) => `Add ${item}`,
    assign: (item: string) => `Assign ${item}`,
    book: (item: string) => `Book ${item}`,
    cancel: (item: string) => `Cancel ${item}`,
    close: (item: string) => `Close ${item}`,
    continue: 'Continue',
    create: (item: string) => `Create ${item}`,
    debug: 'Debug',
    delete: (item: string) => `Delete ${item}`,
    display: (item: string) => `Display ${item}`,
    displaySelectedOnly: 'Display selected only',
    download: 'Download',
    downloadAsCSV: 'Download as CSV',
    downloadAsJSON: 'Download as JSON',
    duplicate: (item: string) => `Duplicate ${item}`,
    edit: (item: string) => `Edit ${item}`,
    export: (item: string) => `Export ${item}`,
    loading: (item: string) => `Loading ${item}`,
    place: (item: string) => `Place ${item}`,
    placeAndBook: 'Place & Book',
    placeAndSend: 'Place & Send',
    reassign: 'Re-assign',
    reload: 'Reload',
    remove: (item: string) => `Remove ${item}`,
    save: (item: string) => ['Save', item].filter(Boolean).join(' '),
    send: (item: string) => `Send ${item}`,
    scan: (item: string) => `Scan ${item}`,
    search: (item: string) => `Search ${item}`,
    select: (item: string) => `Select ${item}`,
    start: (item: string) => `Start ${item}`,
    stopSale: 'Stop sale',
    upload: (item: string) => `Upload ${item}`,
    view: (item: string) => `View ${item}`,
    selected: (item: string) => `${item} selected`,
    loadMore: 'Load more',
  },
  component: {
    picker: {
      startTime: 'Start time',
      endTime: 'End time',
    },
    artistSelect: {
      loading: 'Loading Artists …',
    },
    avatarUpload: {
      dragAndDrop: 'Drag & drop or click to replace',
    },
    intervalPicker: {
      datePresets: {
        today: 'Today',
        yesterday: 'Yesterday',
        weekToDate: 'Week To Date',
        lastWeek: 'Last Week',
        monthToDate: 'Month To Date',
        lastMonth: 'Last Month',
        last30Days: 'Last 30 Days',
        yearToDate: 'Year To Date',
        lastYear: 'Last Year',
        totalPeriod: 'Total Period',
        custom: 'Custom',
      },
      interval: {
        hour: 'Hourly',
        day: 'Daily',
        week: 'Weekly',
        month: 'Monthly',
      },
      selectPeriod: 'Select time period',
      intervalText: 'Interval',
      apply: 'Apply',
    },
    salesOverTime: {
      title: 'Sales Over Time',
      day: 'Daily Sales',
      week: 'Weekly Sales',
      month: 'Monthly Sales',
      hour: 'Hourly Sales',
      totalSales: 'Total Sales',
    },
    fileUpload: {
      selectFile: 'Select File',
    },
    pagination: {
      linesPerPage: 'Lines per page',
      outOf: (current: string | number, total: string | number) => `${current} out of ${total}`,
    },
    confirmation: {
      title: 'Warning',
      text: 'Are you sure you want to perform this action?',
      confirm: 'Confirm',
      cancel: 'Cancel',
    },
    buildCheck: {
      outdatedVersionTitle: 'Outdated version',
      outdatedVersionDescription:
        'It seems like you’re using an outdated version of the app. Reload the page to update to the newest version.',
      outdatedVersionButton: 'Reload App',
    },
    heatmap: {
      allSales: 'All Sales',
    },
    turnover: {
      title: 'Turnover',
      noData: 'No data to show for the selected timeframe',
      totalSales: 'Total Sales',
      total: 'Total',
      period: {
        day: 'Daily Sales',
        hour: 'Hourly Sales',
        month: 'Monthly Sales',
        week: 'Weekly Sales',
      },
    },
    ordersPlaced: {
      title: 'Orders placed',
      noData: 'No data to show for the selected timeframe',
      total: 'Total',
      tooltip: 'All placed orders. Excluding returned or canceled orders.',
      period: {
        day: 'Daily Orders',
        hour: 'Hourly Orders',
        month: 'Monthly Orders',
        week: 'Weekly Orders',
      },
    },
    averageOrderValue: {
      title: 'Average Order Value',
      noData: 'No data to show for the selected timeframe',
      period: {
        day: 'Daily Average',
        hour: 'Hourly Average',
        month: 'Monthly Average',
        week: 'Weekly Average',
      },
      tooltip:
        'Only calculated based on your merchandise products. Orders might contain other merchandise items, if for example sold through krasserstoff.com or in another shop by moving cart. Orders might also contain tickets. Other Merchandise, Tickets, and shipping, are disregarded in this calculation. Based on net prices.',
    },
    sessions: {
      title: 'Shopping Sessions',
      tooltip: 'A Shopping Session is a set of actions performed by one user in a given time frame.',
      total: 'Total',
      period: {
        day: 'Daily Visitors',
        hour: 'Hourly Visitors',
        month: 'Monthly Visitors',
        week: 'Weekly Visitors',
      },
      noData: 'No data available for the selected timeframe',
    },
    countries: {
      title: 'Countries',
      turnover: 'Turnover',
      orderCount: 'Order count',
      sessionCount: 'Session count',
      noData: 'No data available for the selected timeframe',
      noCountry: 'N/A',
    },
    pointsOfSale: {
      title: 'Point of Sale',
      turnover: 'Turnover',
      averageOrderValue: 'Average Order Value',
      orderCount: 'Order count',
      noData: 'No data available for the selected timeframe',
      otherShop: 'Other (e.g. krasserstoff.com, Customer Support)',
    },
    products: {
      title: 'Products',
      turnover: 'Turnover',
      sales: 'Sales Volume',
      noData: 'No data available for the selected timeframe',
    },
    productImage: {
      downloadOriginal: 'Download Original File',
    },
    meta: {
      card: {
        title: 'Meta',
      },
      modal: {
        title: 'Edit Meta',
        fieldName: 'Meta',
      },
    },
    userLoginBarcode: {
      title: 'Login Barcode',
      barcodeExists: 'The user has a login barcode set. You can download the barcode image with the download button.',
      noBarcode: 'No login barcode set',
      noPermission: 'You do not have permission to view the login barcode.',
      download: 'Download Barcode Image',
      generate: {
        title: 'Generate Login Barcode',
        areYouSure:
          'Are you sure you want to generate a new login barcode? This will invalidate the previous barcode, if it existed.',
      },
      remove: {
        title: 'Remove Login Barcode',
        areYouSure: 'Are you sure you want to remove the login barcode? This will render the code invalid.',
      },
    },
  },
  errors: {
    errorLoadingPage:
      'There was an error trying to load the page. Please contact the administrator or try again later.',
    errorLoadingItem: (item: string) => `There was an error loading ${item}`,
    errorLoadingList: 'There was an error loading the items for this list.',
    errorPublishRange: "publish date can't be later than start date",
    errorStartEndRange: "start date can't be later than end date",
    generalError: 'Something Happened, Please try again later.',
    invalidLink: 'Invalid reset token, please contact an admin.',
    invalidUserMsg: 'Incorrect email or password',
    itemAlreadyScanned: 'Item already scanned.',
    itemNotFound: (item: string) => `${item} not found`,
    itemNotInDelivery: 'Item not in delivery.',
    itemNotInList: 'This item is not in the list',
    moreResults: (amount?: string | number) =>
      `${amount ? `${amount} more results` : 'There are more results'}. Try to limit your search.`,
    noItemsforThisCollection: (item: string, collection: string) => `No ${item} in this ${collection}`,
    noResults: 'Sorry, no results match your search query',
    notFound: 'Not found',
    notValidJSON: 'Please enter a valid JSON',
    offline: 'Please check your internet conncection.',
    passwordMismatch: "Passwords Don't match",
    renderError: 'There was an error',
    required: "This field can't be empty.",
    submitError: 'There was an error while trying to submit',
    uploadFile: 'There was an error while trying to upload the file',
    uploadImage: 'There was an error uploading the image',
    noPossibleActions: 'No possible actions',
    notTrackingCode: (query: string) => `The scanned label ${query} doesn't seem to be a tracking code.`,
    'invalid-website-format': 'Must be a valid URL',
    'invalid-email-address': 'Must be a valid email address',
    'required-field': "This field can't be empty.",
    'password-missmatch': 'Passwords must be the same.',
    'invalid-json': 'Please enter valid JSON.',
    'regex-failed': 'Please match the format requested.',
    noShopAssigned: 'no shop assigned',
    requiredVariantsDescription: 'All variants should have a description.',
    voucher_codes_min_1: 'The number of voucher codes must be more than 0.',
    voucher_codes_max_1000: 'The maximum number of voucher codes is 1000.',
  },
  refund: {
    success: 'Refund successful',
    maxAmount: (amount: number) => `Refund amount should be between 0 and ${amount}`,
    amount: 'Amount',
    title: 'Refund Payment',
    attention: 'Attention:',
    description:
      'If you click on "refund" the amount that was entered is directly transferred back to the customers account.',
    refund: 'refund',
  },
  general: {
    link: 'Link',
    unlink: 'Unlink',
    and: 'and',
    absolute: 'Absolute',
    actions: 'Actions',
    all: 'All',
    amount: 'Amount',
    archived: 'Archived',
    backHome: 'Back to Home',
    cantBeEdited: `This field can't be edited`,
    collection: 'collection',
    color: 'Color',
    confirmDelete: (item: string) => `Are you sure you want to delete this ${item}?`,
    confirmDuplicate: (item: string) => `Are you sure you want to duplicate this ${item}?`,
    confirmStopSale: 'Are you sure you want to stop the sale?',
    content: 'Content',
    count: 'Count',
    day: 'Day',
    description: 'Description',
    details: 'Details',
    english: 'English',
    englishShort: 'EN',
    example: 'i.e.',
    error: 'Error',
    feature: 'Features',
    footer: 'footer',
    generalInformation: 'General Information',
    german: 'German',
    germanShort: 'DE',
    headline: 'headline',
    itemsForsCollection: (item: string, collection: string) => `${item} for ${collection}`,
    itemsInThisCollection: (item: string, collection: string) => `${item} in this ${collection}`,
    legacyId: 'Legacy ID',
    locale: 'Locale',
    logout: 'Logout',
    material: 'Material',
    more: (amount: string | number, collection: string) => `${amount} results in ${collection}`,
    motive: 'Motive',
    name: 'Name',
    new: 'New',
    next: 'Next',
    no: 'No',
    noData: 'No Data',
    noDataToShow: 'No data to show',
    notArchived: 'Not Archived',
    now: 'Now',
    open: 'Open',
    optional: 'Optional',
    overview: 'Overview',
    percentage: 'Percentage',
    percent: 'Percent',
    productSafetyDescription: 'Product Safety Description',
    rate: 'Rate',
    reactivate: 'Reactivate',
    refund: 'Refund',
    return: 'Return',
    row: 'Row',
    rows: 'Rows',
    saved: 'Saved',
    selected: 'Selected',
    showing: 'Showing',
    since: 'since',
    slug: 'Slug',
    start: 'Start',
    sendViaEmail: 'Send via Email',
    subtitle: 'Subtitle',
    success: 'Success',
    text: 'Text',
    title: 'Title',
    total: 'Total',
    type: 'Type',
    yes: 'Yes',
    url: 'URL',
    withoutNotification: 'without notification',
    lifeTime: 'Life Time',
    lastSixMonths: '6 Mo Avg.',
    lastThirtyDays: 'Last 30 Days',
    today: 'Today',
    lastHour: 'Last Hour',
    connectedToAPI: (url: string) => `You are currently connected to ${url}`,
    seeMore: 'See more',
    apiToken: 'API Token',
    calendarFeedUrl: 'iCal Feed',
    notAvailable: 'N/A',
    genders: {
      soldToMUsers: 'Male',
      soldToFUsers: 'Female',
      soldToXUsers: 'Unknown / Gender Neutral',
    },
    rowsPerPage: 'Rows Per Page:',
    warning: 'Warning',
  },
  imports: {
    success: 'Imported successfully',
    error: 'Import failed',
  },
  model: {
    ticketEmailTemplate: {
      cardTitle: 'Customize Email Template',
      tags: {
        custom: 'Custom',
        default: 'Default',
      },
      emailLabel: 'Reply to Email',
      noEmailSet: 'No reply-to email set',
      previewLabel: 'Preview',
      previewEmail: 'Preview Email',
      emailTemplate: 'Email Template',
      contentAndStylePreview: 'Content and Style Preview',
      modal: {
        title: 'Customize Email Template',
        emailLabel: 'Reply-to email',
        fieldLabel: 'Email Address',
        switcher: {
          label: 'Use custom email template?',
          yes: 'yes',
          no: 'no',
        },
        templateVariables: 'Template Variables',
        firstName: 'First name',
        lastName: 'Last name',
        email: 'Email',
        phone: 'Phone',
        ticketCount: 'Ticket count',
        emailText: 'Email Text',
      },
    },
    activity: {
      _plural: 'Activities',
      reply: 'reply',
      showDetails: 'Show Details',
      type: {
        activateProductOnKulturpass: ({ modifications: { active } }: { modifications: { active: boolean } }) =>
          active ? 'Activated offer on Kulturpass' : 'Deactivated offer on Kulturpass',
        archived: 'Archived',
        venueLayoutArchived: 'Venue layout archived',
        venueLayoutUnarchived: 'Venue layout unarchived',
        vatOverrideAccepted: 'Accepted Vat Rate Difference',
        stripeChargePerformed: 'Stripe Payment Performed',
        stripeWebhookProcessed: 'Stripe Webhook Processed',
        itemsAddedToCart: 'Items added to cart',
        addItems: 'Items added to cart',
        createStripePayment: 'Payment requested via Stripe',
        confirmOrderPayment: 'Payment confirmation page shown',
        paymentConfirmationSent: 'Payment confirmation sent via email',
        placeOrder: 'Order placed',
        itemsRemovedFromCart: 'Items removed from cart',
        cartCreated: ({ modifications }: { modifications: { ip?: string; userAgent?: string; source?: string } }) => {
          if (!modifications?.ip) return 'Cart created';

          const source = modifications.source ?? 'unverified';
          const device = modifications.userAgent ?? 'an unknown device';
          return `Cart created from ${modifications.ip} (${source}) using ${device}`;
        },
        cartUpdated: 'Cart updated',
        clearingCreated: ({ modifications: { from, to } }: ClearingCreatedActivity) =>
          from && to
            ? `Clearing from ${formatDate(from) ?? '?'} to ${formatDate(to) ?? '?'} created`
            : 'Clearing created',
        externalValidationCodeImport: ({
          modifications: { duplicates, datasets },
        }: {
          modifications: { duplicates: string[]; datasets: number };
        }) =>
          duplicates
            ? `External codes import was successful, ${datasets} codes were uploaded. ${
                duplicates.length
              } duplicates were found: ${duplicates.join(', ')}`
            : `External codes import was successful, ${datasets} codes were imported.`,
        productContractChanged: 'Contract changed',
        contractAddedToProduct: 'Contract added',
        contractRemovedFromProduct: 'Contract removed',
        deliveryCreated: 'Delivery created',
        deliverySent: 'Delivery sent',
        deliveryDeleted: 'Delivery deleted',
        invoiceCreated: 'Invoice created',
        invoiceBooked: 'Invoice booked',
        invoiceDeleted: 'Invoice deleted',
        itemsAddedToOrder: 'Items added to order',
        orderPlaced: 'Order placed',
        orderAddedToPicklist: 'Order added to picklist',
        itemsRefunded: 'Items refunded',
        paypalPaymentConfirmed: 'PayPal payment confirmed',
        paypalPaymentConfirmationFailed: 'PayPal payment confirmation failed',
        paypalWebhookProcessed: 'PayPal webhook processed',
        stripeSessionCompleted: 'Stripe session completed',
        stripeChargeFailed: 'Stripe charge failed',
        userChanged: 'Customer updated',
        userRemoved: 'Customer removed',
        incomingItemsRemoved: 'Incoming items removed',
        outgoingItemsRemoved: 'Outgoing items removed',
        unmarkedProblematic: 'Unmarked Problematic',
        markedProblematic: 'Marked Problematic',
        paymentAddedByAdmin: 'Payment Added',
        preferredPaymentMethodUpdated: 'Preferred Payment Method Updated',
        autoCancelled: 'Auto Cancelled',
        contingentChange: 'Contingent Change',
        refundEmailSent: 'Refund Email Sent',
        itemsMassRefunded: 'Items Mass Refunded',
        seatsBlocked: 'Seats blocked',
        seatsUnblocked: 'Seats unblocked',
        saleStarted: 'Sale started',
        saleStopped: 'Sale stopped',
        unarchived: 'Unarchived',
        applyVoucherCode: 'Voucher code applied',
        removeVoucherCode: 'Voucher code removed',
        userAddedToCompany: ({ modifications: { userName, email, role } }: UserAddedToCompanyActivity) =>
          `added ${userNameAndOrEmail(userName, email)} as ${I18n.translate.model.role[role]} to the team`,
        userRemovedFromCompany: ({ modifications: { userName, email } }: UserRemovedFromCompanyActivity) =>
          `removed ${userNameAndOrEmail(userName, email)} from the team`,
        membershipChanged: ({ modifications: { userName, email, role } }: MembershipChangedEvent) =>
          role
            ? `changed the role of ${userNameAndOrEmail(userName, email)} to ${I18n.translate.model.membership[role]}`
            : `changed membership of ${userNameAndOrEmail(userName, email)}`,
        changeEventVenueLayout: 'Venue layout of the event changed',
        fixClearingRefunds: 'Automatically fixed uncleared refunds',
        deliveryConfirmationSent: 'Delivery confirmation sent',
        venueLayoutCreated: ({ modifications: { name } }: { modifications: { name: string } }) =>
          `Venue layout "${name}" was created`,
        destroyVenueLayout: ({ modifications: { name } }: { modifications: { name: string } }) =>
          `Venue layout "${name}" was deleted`,
        updateVenueLayout: 'Venue layout was updated',
        venueLayoutImportDone: 'Venue layout import was completed with success',
        venueLayoutImportFailedNoChart: 'Venue layout import failed because no charts with the provided ID were find.',
        unbookInvoice: 'Invoice unbooked',
        eventSaleStarted: ({ createdAt, modifications: { publishedFrom, salesStats } }: EventSaleChangeActivity) =>
          publishedFrom && publishedFrom > createdAt
            ? `has schedule the sale for ${formatDate(publishedFrom)}.`
            : `has started the sale. At this time there were ${salesStats.systemSold} tickets sold.`,
        eventSaleRequested: ({ createdAt, modifications: { publishedFrom, salesStats } }: EventSaleChangeActivity) =>
          publishedFrom && publishedFrom > createdAt
            ? `has requested the sale for ${formatDate(publishedFrom)}.`
            : `has requested the sale. At this time there were ${salesStats.systemSold} tickets sold.`,
        eventSaleStopped: ({ modifications: { salesStats } }: EventSaleChangeActivity) =>
          `has stopped the sale. At this time there were ${salesStats.systemSold} tickets sold.`,
        inviteUser: ({ modifications: { invitedEmail, role } }: InviteUserActivity) =>
          `invited ${invitedEmail} to join the Team as an ${I18n.translate.model.role[role]}`,
        createPricingCategory: ({ modifications: { name } }: { modifications: { name: string } }) =>
          `Pricing category "${name}" was created`,
        destroyPricingCategory: ({ modifications: { name } }: { modifications: { name: string } }) =>
          `Pricing category "${name}" was deleted`,
        updatePassbookConfig: ({ modifications: { enabled } }: { modifications: { enabled: boolean } }): string =>
          enabled ? 'Wallet Ticket generation enabled' : 'Wallet Ticket generation disabled',
      },
      customCommentActivity: {
        seatsBlocked: (reason: string) => `Seats blocked with reason "${reason}"`,
        seatsUnblocked: `Seats unblocked`,
      },
      writeComment: 'Write a comment',
      writeReply: 'Write a reply',
      shipmentRelevant: 'Shipment relevant',
      markAsShipmentRelevant: 'Mark as shipment relevant',
      productImportant: 'Important to the product',
      markAsProductImportant: 'Mark as important to the product',
      comment: 'Comment',
      comments: 'Notes / Comments',
      errors: {
        noEvent: "Can't find Event",
        noProduct: "Can't find Event",
        noEventOrProduct: "Can't find Event or Product",
      },
      contingentChangeMessage: ({ from, to, delta }: { from: number; to: number; delta: number }) =>
        `from ${from} to ${to} (Difference of ${delta})`,
      preferredPaymentMethodUpdate: ({ from, to }: { from: string; to: string }) => `from ${from} to ${to}`,
      blockedSeats: {
        allSeats: 'All Seats',
        row: 'Row',
        seats: 'Seats',
      },
      processPaymentAuthorization: {
        capture: 'Payment captured with authorization',
        release: 'Payment authorization released',
      },
    },
    address: {
      _name: 'Address',
      additionalStreet: 'Additional Street',
      capacity: 'Capacity',
      city: 'City',
      country: 'Country',
      postCode: 'Post Code',
      street: 'Street',
    },
    agreement: {
      _name: 'Agreement',
      _plural: 'Agreements',
      title: 'Title',
      partner: 'Contracting partner',
      description: 'Description',
      postTax: 'Calculate with',
      vatOverride: 'Vat Override',
      type: 'Type',
    },
    allocation: {
      _name: 'Allocation',
      _plural: 'Allocations',
      allocationList: 'Allocation List',
      csv: 'Allocation CSV',
      downloadPdf: 'Download Allocation PDF',
    },
    artist: {
      _name: 'Artist',
      _plural: 'Artists',
    },
    bank: {
      _name: 'Bank',
      amount: 'Amount',
      bankDetails: 'Bank Details',
      bankName: 'Bank Name',
      bankTransfer: 'Bank Transfer',
      bic: 'BIC',
      cod: 'Cash On Delivery',
      holdersName: "Bank account holder's name",
      iban: 'IBAN',
      paypal: 'PayPal',
      amazon: 'Amazon Pay',
      shopify: 'Shopify',
      recipient: 'Recipient',
      stripeCard: 'Stripe Card',
      stripeSofort: 'Stripe Sofort',
      stripeCheckout: 'Stripe Checkout',
      paypalExpress: 'PayPal Express',
      stripeExpress: 'ApplePay / GPay',
      taxNumber: 'Tax Number',
      vat: 'VAT ID',
      vatId: 'EU VAT ID',
    },
    bookkeeping: {
      _name: 'Book Keeping',
      bookKeepingExport: 'Book Keeping Export',
    },
    brand: {
      _name: 'Brand',
      _plural: 'Brands',
    },
    label: {
      _name: 'Label',
      _plural: 'Labels',
    },
    category: {
      _name: 'Category',
      _plural: 'Categories',
      abstract: 'Abstract',
      altNames: 'Alternative Names',
      altNamesDesc: 'Comma separated list of alternative names',
      featuredAt: 'Featured At',
      mainCategory: 'Main Category',
      sortOrderFeatureBottom: 'Sort order feature / bottom',
      sortOrderFeatureTopLeft: 'Sort order feature / top left',
      sortOrderFeatureTopRight: 'Sort order feature / top right',
      orderTooltip: 'The position of the category in a list of categories. For example in a band shop main page.',
      orderHomepageTooltip:
        'The position of the category in the featured section of categories on the main shop start page. Higher numbers get placed first. Leave empty or set to -1 to hide.',
      orderTicketTopLeftTooltip:
        'The position of the category in the top left featured section in the main shop, either in /merch, /tickets or /artists, depending on the type of category. Higher numbers get placed first. Leave empty or set to -1 to hide.',
      orderTicketTopRightTooltip:
        'The position of the category in the top right featured section in the main shop, either in /merch, /tickets or /artists, depending on the type of category. Higher numbers get placed first. Leave empty or set to -1 to hide.',
      orderTicketBottomTooltip:
        'The position of the category in the lower featured section in the main shop, either in /merch, /tickets or /artists, depending on the type of category. Higher numbers get placed first. Leave empty or set to -1 to hide.',
      slug: 'Slug',
      slugFormat: 'Should only contain numbers or letters separated by a dash. Example: cool-category',
      sort: 'Sort',
      loadingCategory: 'Loading category',
      shopFromParentCategory: 'Is taken from the parent category',
      meta: 'Meta',
    },
    charts: {
      _name: 'Charts',
      reports: 'Reports',
      date: 'Date',
      fileName: 'File Name',
      download: 'Download',
      upload: 'Upload',
      uploadChart: 'Upload Chart',
      uploadSuccess: 'Upload Success',
      uploadSuccessful: 'File was successfully uploaded.',
      uploadFailed: 'Upload Failed',
      uploadFailedMessage: 'Unable to upload file.',
      preview: 'preview',
      generatePreview: 'Generate Preview',
      manualUploaded: 'manual',
      markAsResolved: 'Mark as resolved',
      confirmResolvedTitle: 'Mark error as resolved',
      confirmResolved: 'Are you sure that you have resolved the file errors?',
      confirm: 'Confirm',
    },
    company: {
      _name: 'Company',
      _plural: 'Companies',
      accountsPaymentsTaxes: 'Accounts, Payments & Taxes',
      billingAddress: 'Billing Address',
      billingContact: 'Billing contact',
      billingInfo: 'Billing Information can only be changed by users with admin status.',
      currentCompany: 'Current Company',
      invoices: 'We will use this address for invoices and other documents.',
      noBankDetails: 'No registered bank details',
      noRegisteredAddress: 'No registered address',
      vendor: 'Vendor',
      vendorInformation: 'Vendor Information',
      vendors: 'Vendors',
      confirmRemoveCompany: 'Are you sure you want to remove this company?',
      removeCompany: 'Remove company',
      untitledCompany: 'Untitled Company',
    },
    contract: {
      _name: 'Contract',
      _plural: 'Contracts',
      contractLine: 'Contract line',
      postTax: 'Post Tax',
      receiver: 'Reciever',
      vatOverride: 'Vat Override',
      notAuthorized: 'Not authorized to see this contract',
    },
    chartExportTypes: {
      charts: 'Music Charts',
      bookCharts: 'Book Charts',
    },
    countries: {
      de: 'Germany',
      at: 'Austria',
      ch: 'Switzerland',
    },
    datetime: {
      _name: 'Date',
      _plural: 'Dates',
      activeDate: 'Active Date',
      createdAt: 'Created At',
      datesAndTimes: 'dates and times',
      endDate: 'End Date',
      eternity: 'infinity',
      formatHHMM: 'Format: HH:MM',
      invalidatedAt: 'Invalidated At',
      publishDate: 'Publish Date',
      publishDateTime: 'Publish date / time',
      unPublishDate: 'Unpublish Date',
      startDate: 'Start Date',
      timeframe: 'Timeframe',
      updatedAt: 'Updated At',
      week: 'Week',
      duration: 'Event duration',
    },
    delivery: {
      _name: 'Delivery Note',
      _plural: 'Delivery Notes',
      carrier: 'Carrier',
      deleteDelivery: 'Delete Delivery Note',
      delivery: 'Delivery',
      incoming: 'Incoming',
      outgoing: 'Outgoing',
      scanDeliveryOrReference: 'Scan delivery note or enter reference',
      deliveryNoteType: 'Delivery note type',
      deliveryNoteTypes: {
        inventory_correction: 'Inventory correction',
        damage: 'Shrinkage / damage',
        new_goods: 'New goods',
        circulating_goods: 'Circulating goods',
        other: 'Other',
      },
      trackingCode: 'Tracking Code',
      editTrackingCodeAndType: 'Edit Tracking Code and Type',
      trackingLink: 'Tracking link',
      shipping: 'Shipping',
      shippingMethod: 'Shipping method',
      deliveryPlaced: 'Delivery placed',
      deliveryBooked: 'Delivery booked',
      new: 'New Delivery',
      no: 'No Delivery',
      removeFromPicklist: 'Remove from picklist',
      removedFromPicklist: 'Delivery was removed from picklist',
      confirmRemoveFromPicklist: 'Are you sure you want to remove this delivery from its picklist?',
    },
    event: {
      _name: 'Event',
      _plural: 'Events',
      admission: 'Admission',
      admissionStatisticsLink: 'Open shareable admission statistics',
      announcementOn: 'Announcement on',
      billing: 'Billing',
      blockAllSeats: 'Create event with all seats blocked?',
      changeDueToActiveResale:
        'Caution: Ticket codes might change due to re:sale being enabled. Contact us so we might end the re:sale or be aware of "unknown" tickets at the entry.',
      changeDueToProcessing: (count: number) =>
        `Ticket codes might change due to ${count} ${pluralize(
          count,
          'ticket'
        )} being processed. If you reload the list in 10-15 minutes, all ticket codes should be included.`,
      changeDueToCart: (count: number) =>
        `Ticket codes might change due to ${count} ${pluralize(
          count,
          'ticket being in a cart',
          'tickets being in carts'
        )}. If you reload the list in 10-15 minutes, all ticket codes should be included`,
      changeDueToReserved: (count: number) =>
        `Ticket codes might change due to ${count} ${pluralize(
          count,
          'ticket being reserved. If this ticket is booked, it becomes a valid ticket, but is not included in the code list.',
          'tickets being reserved. If these tickets are booked, they will become valid tickets. However, the codes are not included in the code list'
        )}`,
      ticketcodes: 'Ticketcodes',
      changeOwner: 'Change Owner',
      chartsKey: 'Chart Key / URL to JSON',
      chartsScale: 'Chart Scale',
      chartsScaleDescription: 'The imported venue layout will be scaled by this factor.',
      close: 'Close',
      confirmRequestMsg:
        'You’re about to confirm that you’re the promoter of this event and request a review and sale permission. After a successful review this event will go live on the selected launch date. Do you want to continue?',
      doorTime: 'Door Time',
      draftMsg: 'As soon as you entered all the necessary information, you can request the sale here.',
      deleteEventTitle: 'Delete Event',
      deleteEventConfirmation: 'Are you sure you want to delete this event?',
      displayingTourImage: '* Image taken from the tour',
      externalReference: 'External reference',
      eventDetails: 'Event details',
      eventId: 'Event ID',
      eventTitle: 'Event title',
      excludeFromFeeds: 'Exclude from Feeds (Facebook & Google)',
      externalCodesUpload: 'Upload External Codes',
      externalCodesUploadDescription:
        'Here you can upload a CSV file with external ticket codes. The file must have the following format (one code per line): Only one column with the code, no header line.',
      externalCodesUploadSuccessModal: 'Upload externer Codes',
      externalCodesUploadSuccessModalDescription: (duplicates: string[]) =>
        `Import successful. ${duplicates.length} duplicates have been found: `,
      happeningOn: 'The event is happening on the',
      includePublicTransportInfo: 'Include public transport information',
      inReviewMsg:
        'Lay back and play your favorite record while we’re reviewing your event and prepare the sale. Keep in mind that we’re only reviewing events on Mon - Fri, 10am - 5pm.',
      instantPaymentLabel: 'Instant Payments Threshold',
      instantPaymentDescription: {
        only: 'only instant payments are allowed',
        all: 'all payment methods are allowed',
        custom: 'custom setting based on availability',
      },
      instantPaymentCustomDescription:
        '0% means that only instant payments are allowed\ne.g. 50% means that after 50% of sold tickets, only instant payments are allowed\n100% means that all payment methods are allowed until event is sold out',
      localOperator: 'Local operator',
      publishedUntillWarning: (publishedUntilLabel: string) =>
        `Note: <strong>${publishedUntilLabel}</strong> isn’t matching the default value`,
      updatePublishedUntil: (publishedUntilLabel: string, defaultPublishedUntilString: string) =>
        `Set ${publishedUntilLabel} to ${defaultPublishedUntilString}`,
      massRefund: {
        _name: 'Mass Refund',
        massRefundable: 'Mass Refundable?',
        autoBookRefunds: 'Automatically Book mass refunds?',
        usedTicketsMassRefundable: 'Enable mass refunds for used Tickets?',
        emailTemplateDe: 'Email Template DE',
        emailTemplateEn: 'Email Template EN',
        emailSubjectDe: 'Email Subject DE',
        emailSubjectEn: 'Email Subject EN',
        customTitleDe: 'Mass Refund Custom Title DE',
        customTitleEn: 'Mass Refund Custom Title EN',
        refundExplanationDe: 'Mass Refund Explanation Text DE',
        refundExplanationEn: 'Mass Refund Explanation Text EN',
        triggerMassRefund: 'Trigger Mass Refund',
        pauseMassRefund: 'Pause Mass Refund?',
        sendEmailTo: 'Are you really really really sure that you want to send an email to',
        peopleNum: (num: number) => `${num} People`,
        withInstructions: 'with instructions to refund their tickets?',
        ticketRefundTool: 'Ticket Refund Tool',
        linkToRefundPage: 'Link To Refund Tool',
        sendEmailWithTemplate: 'Send Email With Template',
        sendRefundEmail: 'Send Refund Email',
        refundTriggeredAt: 'Refund Triggered At',
        refundPaused: 'Refund Paused',
        refundSuccessful: 'Mass Refund Triggered Successfully',
      },
      meta: 'Meta',
      noPaymentThresholdSet: 'No payment Threshold set',
      organizer: 'Organizer',
      owner: 'Owner',
      paymentOptions: 'Payment Options',
      paymentThreshold: 'Threshold for instant payments only',
      productVariantsActiveFromWarning:
        'This will override the date from which every product variant is active. There are already different values set and setting this will overwrite them.',
      promoter: 'Promoter',
      publicTransportDescription: 'Public Transport Description',
      publicTransportDescriptionDefault:
        'The ticket price includes a public transport ticket. However, the admission ticket is not valid as a ticket. Please scan the QR code to receive your ticket or visit https://krasserstoff.com/opnv',
      publishAt: 'Publish at',
      resale: {
        allow: 'Allow',
        editSettings: 'Edit Settings',
        confirm: 'Confirm',
        info: 're:sale Settings',
        off: 'Off',
        onSoldOut: 'Allow only when all regular tickets are sold out',
        privateLink: 'Private link',
        publicOffer: 'Public offer',
        resalePreference: 'Resell Preference',
        tooltip: 'Ticket reselling will allow/not allow the fans to resell their purchased tickets.',
        visibilitySettings: 'Visibility Settings',
        visibilities: {
          privateLink: 'Private link',
          publicOffer: 'Public offer',
        },
        visibilitySettingsRequired: 'At least one visibility setting is required',
        preferences: {
          allow: 'Allow',
          onSoldOut: 'Allow only when all regular tickets are sold out',
          off: 'Off',
        },
        offer: {
          cardHeader: 'Tickets',
          orderReference: 'Order reference',
          onOfferSince: 'On offer since',
          validationCode: 'Validation code',
          status: 'Status',
          visibility: 'Visibility',
          privateLink: 'Private link',
          publicOffer: 'Public offer',
          resaleOffer: 're:sale Offer',
          purchased: 'purchased',
          cart: 'cart',
          available: 'available',
        },
        salesOverTime: 'Sales Over Time',
      },
      sendRequest: 'Send request',
      shopContent: 'Shop/Content',
      shopSettings: 'shop settings',
      ShopCategories: 'Shop/Tour/Artist',
      ShopCategoriesModalTitle: 'Shop/Tour/Artist Association',
      noCategoryAssociation: 'No associations created',
      shopTours: 'Shops/Tours',
      showArchived: 'Show archived venue layouts',
      sku: 'SKU',
      soldOutMsg: 'Sold out message',
      slug: 'Slug',
      slugFormat: 'Should only contain numbers or letters separated by a dash. Example: cool-event',
      stageTime: 'Stage Time',
      stats: 'Stats',
      ticketCodeUsageUpload: 'Upload Ticket Code Usage',
      ticketCodeUsageUploadDescription:
        'Upload a csv file containing columns for the validation code, time of usage (optional) and scanner identifier (e.g. "Entry A, Scanner a", optional) to invalidate tickets.',
      ticketCodesButton: 'Download Ticketcodes',
      fullTicketCodesButton: 'Download Ticketcodes with meta',
      ticketCodesMsg: 'You have to stop the event to download the ticketcodes',
      tourOperator: 'Tour operator',
      untitledEvent: 'Untitled Event',
      updateSoldOutMsg: 'Update sold out message',
      updateOwningCompany: 'Update owning company',
      utmTrackingCampaigns: {
        title: 'UTM Tracking Campaigns',
        tooltip: 'Track the top 10 campaigns for this event',
      },
      utmTrackingSources: {
        title: 'UTM Tracking Sources',
        tooltip: 'Track the top 10 sources for this event',
      },
      utmTrackingMedia: {
        title: 'UTM Tracking Media',
        tooltip: 'Track the top 10 media for this event',
      },
      noSeatsAvailable: 'No seats available in this section',
      billingItem: 'Billing Item',
      personalization: {
        _name: 'Personalization',
        noPersonalizationRequired: 'No personalization',
        groupPersonalizationRequired: 'Group personalization (same name for all tickets)',
        ticketPersonalizationRequired: 'Ticket personalization (one name per ticket)',
        contactTracingRequired: 'Contact Tracing',
        checkInRequired: 'Check-In',
        bdkv: 'BDKV Personalization',
      },
      importantCommentsTitle: 'WARNING: Important Notes for This Event',
      scannerCodeTypes: 'Scanner Code Types',
      noScannerCodeTypes: 'No scanner code types allowed for this event.',
      _codeTypes: {
        qr: {
          label: 'QR',
          description: '2d QR Code',
        },
        ean: {
          label: 'EAN',
          description: '1d EAN Barcode',
        },
      },
    },
    scanner: {
      _name: 'scanner',
      _plural: 'Scanner',
      addNew: 'Add new scanner',
      delete: 'Delete scanner',
      loadingQR: 'Loading QR code',
      errorQR: 'QR code could not be generated for this scanner',
    },
    image: {
      _name: 'Image',
      _plural: 'Images',
      imageUrl: 'Image URL',
      square: 'Image (square)',
      landscape: 'Image (landscape)',
      portrait: 'Image (portrait)',
    },
    invoice: {
      _name: 'Invoice',
      _plural: 'Invoices',
      bookedAt: 'Booked at',
      bookingNumber: 'Booking Number',
      customerData: 'Customer Data',
      billingEmails: 'Billing Emails',
      invoicePlaced: 'Invoice placed',
      invoiceBooked: 'Invoice booked',
      deleteInvoice: 'Delete Invoice',
      unbookInvoice: 'Unbook Invoice',
      unbookInvoiceMessage: 'Please confirm you want to unbook this clearing invoice.',
      unbookInvoiceWarning:
        'WARNING: If items are billed twice (e.g. event items), both invoices have to be unbooked or it might lead to double billing items.',
      no: 'No Invoice',
      new: 'New Invoice',
      downloadDetails: 'Download Details',
      invoicing: 'Invoicing',
      invoicingCycle: 'Merch Invoicing Cycle',
      eventPayoutCycle: 'Event Payout Cycle',
      lastEventPayout: 'Last Event Payout',
      defaultEventPayoutPercentage: 'Default Event Payout %',
      invoicingCycles: {
        none: 'None',
        weekly: 'Weekly',
        monthly: 'Monthly',
        quarterly: 'Quarterly',
      },
      lastClearingInvoice: 'Last Merch Clearing Invoice',
      clearingEmailSubject: (invoice: string) => `Clearing Invoice ${invoice}`,
      clearingEmailBody: (link: string) =>
        `We have created a new statement. This can now be downloaded from the following link. The link may not be valid indefinitely. Therefore, we recommend downloading the statement and archiving it locally.\n${link}`,
      previousInvoicePdf: 'Link to PDF',
    },
    item: {
      _name: 'Item',
      _plural: 'Items',
      itemCount: (count: number) => `${count} ${pluralize(count, 'Item', 'Items')}`,
      createdAt: 'Added',
      price: 'Price',
      quantity: 'Quantity',
      unknownItem: 'Unknown Item',
      vatPercentage: 'VAT Percentage',
      validationCode: 'Validation Code',
      usedAt: 'Used at',
      removeModal: {
        removeAndRelease: 'Remove and release',
        removeAndBlock: 'Remove and block',
        ticketOptions: (tickets: number) => `Removal options ${pluralize(tickets, 'Ticket')}`,
        mixedWarning: (tickets: number) =>
          `You’re removing a mixed selection of products and ${pluralize(tickets, 'ticket')}.`,
      },
    },
    news: {
      _name: 'News Item',
      _plural: 'News',
      untitled: 'Untitled news',
      regular: 'Regular',
      hotNews: 'Hot news',
      featureNews: 'Featured News',
      inverse: 'Inverse',
      inverseDescription: 'Inverse mode is used to display white text over dark images in the shop.',
      deleteNewsItem: {
        title: 'Delete news item',
        description: 'Are you sure you want to delete this news item?',
        confirm: 'Confirm',
      },
      order: 'Order',
      image_video: 'Image/Video',
      meta: 'Meta',
    },
    logo: {
      _name: 'Logo',
      _plural: 'Logos',
      presenterLogo: 'Presenter Logo',
    },
    membership: {
      _name: 'Membership',
      _plural: 'Memberships',
      admin: 'Admin',
      editor: 'Editor',
      merchAdmin: 'Merch Admin',
      financialAdmin: 'Finance',
      financialAdminDescription: 'The finance permission allows access to the dashboard statistics.',
      observer: 'Observer',
      owner: 'Owner',
      role: 'Role',
      copyToken: 'Copy API Token to Clipboard',
      copyConfirmation: 'The token was copied to the clipboard.',
      regenerateToken: 'Regenerate API Token',
      regenerateTokenConfirmation:
        'This will invalidate the current token and disconnect all connected APIs for this membership. Are you sure you want to continue?',
      copyCalendarFeedUrlToClipboard: 'Copy the Calendar Feed URL to the Clipboard',
      copyCalendarFeedUrlToClipboardConfirmation: 'The Calendar Feed URL was copied to the clipboard.',
      ticketingAdmin: 'Ticketing Admin',
      inviteNewUser: 'Invite a new user',
      addRegisteredUser: 'Add a registered user',
      editRegisteredUser: 'Edit a registered user',
      editInvitation: 'Edit invitation',
      sendInvite: 'Send the invite',
      invitedExpires: (createdAt?: string, expiresAt?: string) => {
        return `(${[
          createdAt && `Invited on ${formatDate(createdAt)}`,
          expiresAt && `Expires on ${formatDate(expiresAt)}`,
        ]
          .filter(Boolean)
          .join('. ')})`;
      },
      invitationExpired: 'Invitation Expired',
      awaitingResponse: 'Awaiting invite response',
      dateOfInvite: 'Date of invite',
      linkExpiration: 'Link expiration',
      noLongerAMember: 'Sorry, you have no more access to this page as you are no longer a member',
      invalidLink:
        'The invite link you used does not seem to be valid. Please contact the person that invited you to send another invite.',
      expiredLink: (invitedBy: string) =>
        `Sorry, but the invite expired. Please contact ${invitedBy} to send you a new invite.`,
      invitedEmailMismatch: (invitedEmail: string, currentUserEmail: string) =>
        `This invite was sent to ${invitedEmail}, but you are logged in with ${currentUserEmail}.`,
      membershipChanged: {
        title: 'Company Change',
        message: 'Reloaded after company change in another tab',
      },
    },
    order: {
      _name: 'Order',
      _plural: 'Orders',
      autoCancelAt: 'Auto Cancellation',
      balance: 'Balance',
      deliveries: 'Deliveries',
      exportFulfillmentLists: 'Export fulfillment lists',
      incomingProducts: 'Incoming Product',
      items: ':count: items',
      invoiceAddress: 'Invoice Address',
      shippingAddress: 'Shipping Address',
      massUpdateTags: 'Add & change tags',
      noItems: 'This order has no items.',
      orderId: 'Order Id',
      selectedOrder: 'selected order',
      selectedOrders: 'selected orders',
      stocking: 'Stocking Note',
      storageLocation: 'Storage Location',
      noStorageLocation: 'No storage location',
      tags: 'Tags',
      ticketList: 'Ticket list',
      user: 'Customer',
      addUserToOrder: 'Change Customer',
      editCancelAt: 'Edit Cancel At',
      withShippableProductsOnly: 'With shippable products only',
      includeArchive: 'Include Archive',
      exportEmails: 'Export customer emails',
      setProblem: 'Set problem status',
      setProblemMsg: 'Are you sure you want to set this order\'s "Problem" status?',
      unsetProblem: 'Unset problem status',
      unsetProblemMsg: 'Are you sure you want to unset this order\'s "Problem" status?',
      startClearingCycle: 'Start Clearing Cycle',
      createAnother: 'create another',
      deleteOrderTitle: 'Delete Order',
      deleteOrderConfirmation: 'Are you sure you want to delete this order?',
      orderDeleted: 'Order deleted',
      importantInformationAboutProducts: 'Important information about the product: ',
      copyPaymentLink: 'Copy payment link to the clipboard',
      copyPaymentLinkConfirmation: 'The payment link was copied to the clipboard.',
      editSuccess: 'Payment was edited successfully',
      editFailed: 'An error was encountered while editing payment',
      containingOnly: 'Containing Only',
      resendOnlineTickets: 'Resend Online Tickets',
      resendConfirmationMail: 'Resend Confirmation Mail',
      onlyContainingOptions: {
        onlineTicketOnly: 'Online ticket only',
        hardTicketOnly: 'Hard ticket only',
        merchOnly: 'Merch only',
        hardTicketAndMerch: 'Hard ticket and merch',
      },
      shopDetails: {
        title: 'Shop Details',
        shop: 'Shop',
        domain: 'Domain',
        update: 'Change Shop',
        noShop: 'No shop assigned to this order.',
        addedSuccessfully: 'Shop was added successfully',
        removedSuccessfully: 'Shop was removed successfully',
      },
      voucherDetails: {
        title: 'Voucher Details',
        campaign: 'Voucher Campaign',
        code: 'Voucher Code',
        discount: 'Discount',
        enterCode: 'Enter code',
        addedSuccessfully: 'Voucher was added successfully',
        removedSuccessfully: 'Voucher was removed successfully',
        voucherCodeApplied: (code: string) => `The voucher code ${code} might be applied.`,
      },
      showCount: 'Show count',
      close: 'Close',
      ordersCount: 'Orders count',
      capturePayment: {
        title: 'Capture Payment',
        description: 'Are you sure you want to capture the payment for this order?',
        confirm: 'Confirm',
      },
      releasePayment: {
        title: 'Release Payment',
        description: 'Are you sure you want to release the payment for this order?',
        confirm: 'Confirm',
      },
      offerForResale: {
        title: 'Offer for re:sale',
        public: 'Public re:sale',
        bankDetails: 'Bank Details',
        recipient: 'Recipient',
        iban: 'IBAN',
        bic: 'BIC',
        bankName: 'Bank Name',
        confirm: 'Confirm',
        successTicket: 'Ticket successfully offered for re:sale.',
        successTickets: 'Tickets successfully offered for re:sale.',
      },
    },
    backPayments: {
      _title: 'Refunds',
      downloadPending: (type: string) => `Download Pending ${type} Payments`,
      alsoBook: 'Also Book Payments',
      confirmationTitle: 'Download and Book Payments',
      bookingConfirmation: `Are you sure you want to book all pending payments?`,
    },
    payment: {
      _name: 'Payment',
      _plural: 'Payments',
      paymentMethod: 'Payment Method',
      status: 'Status',
      skipCustomerEmail: 'Skip customer email',
      adminPayment: 'Admin Payment',
      markAsPaid: 'Mark as Paid',
    },
    permissions: {
      setPermissionsForThisItem: (item: string) => `Set permissions for this ${item}`,
      setPermissionsForThisShop: 'Set permissions for this shop',
      deleteTitle: 'Delete Ownership',
      deleteConfirmation: 'Are you sure you want to delete this ownership?',
    },
    picklist: {
      _name: 'Picklist',
      _plural: 'Picklists',
      addresslistCSV: 'Address List CSV',
      ticketlistPDF: 'Ticket List PDF',
    },
    price: {
      _name: 'Price',
      _plural: 'Prices',
      _verb: 'Pricing',
      active: 'active',
      inactive: 'inactive',
      allVariants: 'All Variants',
      basePrice: 'Base Price',
      calculatePrice: 'Calculate Price',
      calculationMode: 'VAT Calculation Mode',
      currency: '(€ net)',
      currencyWithVat: '(€ net/gross)',
      currencyGross: '(€ gross)',
      grossPrice: 'Gross Price',
      grossPriceWithVAT: (vat: string) => `Gross price (incl. ${vat} VAT)`,
      editDetails: 'edit details',
      netPrice: 'Net Price',
      brutPrice: 'Brut Price',
      presaleFee: 'Pre-Sale Fee',
      priceParts: 'Aditional Fee',
      systemFee: 'System Fee',
      total: 'Total',
      multipleValues: 'Multiple Values',
      net: 'Net',
      gross: 'Gross',
      addNewPricepart: 'Add new pricepart',
      refundDeduction: 'Refund deduction',
      editTaxes: 'Edit Taxes',
      createPrice: 'Create Price',
      vatRatesEU: 'VAT rates EU (%)',
      vatRateDE: 'VAT rates Germany (%)',
      vatRateEU: 'VAT rate EU (%)',
      vatRateWorld: 'VAT rate World (%)',
      vatRateMismatch:
        "This Vat Rate doesn't match the contract, you can still continue if you know what you’re doing.",
      grossForGermany: 'Gross Price Calculated for Germany',
      clickToExpand: 'Click on the expand icon to edit VAT rates per country',
      nonEU: 'Everything that is not covered by Germany and EU, Defaults to 0%',
      divergingRates: 'Diverging rates',
      sameRates: 'Same rates',
      presetRates: 'Choose from preset Vat Rates',
      customRate: 'Enter Custom Vat Value',
      vatRateMismatchModal: {
        title: "Vat Rate doesn't match Contract",
        confirmationPrompt: (value?: number) => `Are you really sure you want to set the vat rate to ${value || ''}%?`,
        description: "It doesn't match the contract's Vat Override value for the current agreement.",
      },
      vat: 'VAT',
      priceVariant: 'Price Variant',
      originalPrice: 'Original Price',
      originalPriceDescription:
        'When you fill out this field, your product will be displayed as discounted in the shop. This net price will be used as the strikethrough price.',
      originalPriceBrut: 'Original Price (Brut)',
      comment: 'Comment',
      world: 'World',
      resaleBuyerFee: 're:sale Buyer Fee (19% VAT)',
      resaleSellerFee: 're:sale Seller Fee (19% VAT)',
      defaultValue: 'Default Value',
    },
    pricingCategory: {
      _name: 'Pricing Category',
      _nameShort: 'Pricing Cat.',
      _plural: 'Pricing Categories',
      name: 'Name',
      statistics: 'statistics per pricing category',
      seatsCount: 'Seats',
      venueSectionsCount: 'Sections',
      csv: 'Pricing Category CSV',
    },
    product: {
      _name: 'Product',
      _plural: 'Products',
      addContent: 'Add Content',
      additionalInfo: 'Additional Info',
      additionalInfoBellow: 'Additional Info Below the Product',
      additionalInfoSide: 'Additional Info on the Side',
      artistBrand: 'Artist / Brand',
      availableCount: 'Available Count',
      blockedCount: 'Blocked Count',
      bookedCount: 'Booked Count',
      bundleConfigExport: 'Bundle Config Export',
      contentDe: 'Content DE',
      contentEn: 'Content EN',
      contract: 'Contract',
      contractItemOwner: 'Contract / Item Owner',
      costAccount: 'Cost Account',
      deactivateAt: 'Deactivate at',
      draftsCount: 'Drafts Count',
      editGeneral: 'Edit General',
      excludeFromFeeds: 'Exclude from Feeds (Facebook & Google)',
      general: 'General',
      googleFeedCategory: 'Google Feed Category',
      googleFeedCategories: {
        clothing_and_accessoires: 'Clothing & Accessories',
        magazines_and_newspapers: 'Magazines & Newspapers',
        music_and_sound_recordings: 'Music & Sound Recordings',
        posters_prints_and_visual_artwork: 'Posters, Prints & Visual Artwork',
        lighters_and_matches: 'Lighters & Matches',
        print_books: 'Print Books',
      },
      freeShippingMode: {
        title: 'Free Shipping Mode',
        off: 'Don’t offer free shipping, unless another product in the order forces it.',
        unanimous: 'Offer free shipping if all products in the order have this activated too.',
        force: 'Force to offer free shipping, even if other products in the order don’t support it.',
      },
      materialMissingWarning: 'This product is missing material information',
      merch: 'Merch',
      merchandise: 'Merchandise',
      availability: 'Availability',
      minAvailability: 'Min. Availability',
      low: 'Low',
      noContent: 'No content added for this locale. Click the button to start adding content',
      overlayDetail: 'Overlay Detail',
      overlayListing: 'Overlay Listing',
      owner: 'Owner',
      productImageDescription:
        'Here you can upload, re-arrange images for the listings and for the product detail page. You can mark multiple images for the listing e.g. for slider or hover functionality',
      productVariant: 'Product Variant',
      productVariantActivation: 'Product Variant Activation',
      size: 'Size',
      sku: 'SKU',
      slug: 'Slug',
      slugFormat: 'Should only contain numbers or letters separated by a dash. Example: cool-product',
      sortOrder: 'Sort order',
      stock: 'Stock',
      untitledProduct: 'Untitled Product',
      variation: 'Variation',
      weight: 'Weight',
      noMusicChart: 'There is no music charts data for this product.',
      noBookChart: 'There is no book charts data for this product.',
      shippableFrom: 'Dispatch processing from',
      publicShippableFrom: 'Communicated ship date (publicly displayed)',
      fulfillment: 'Fulfillment',
      dhlShippableFrom: 'DHL Label date in the future (only used for bulk processing)',
      customsTariffNumber: 'Customs Tariff Number',
      customVat: 'Custom Vat',
      checkoutOptions: 'Checkout Options',
      instantPaymentRate: 'Instant Payment Rate',
      cartLimit: 'Cart Limit',
      maximumItems: 'Maximum number of items in the cart',
      cantClone: "Can't clone this product because it's a combined product",
      partOfBundle: 'This product is part of a bundle and needs to be combined with other products at checkout.',
      loadingCombinedProducts: 'Loading combined products.',
      possibleCombinations: 'Possible combinations:',
      thisProductAnd: 'This product AND',
      importantCommentsTitle: 'WARNING: Important Notes for This Product',
      startSale: 'Start sale',
      stopSale: 'Stop sale',
      altText: {
        title: 'Alt Text',
        inListing: 'In Listing',
        inGallery: 'In Gallery',
        update: 'Update Alt Text',
        english: 'English Alt Text',
        german: 'German Alt Text',
      },
      meta: 'Meta',
    },
    merchStatistics: {
      _name: 'Statistics',
      productVariants: 'Product Variants',
      productStatus: 'Product Status',
      displayOnlyActive: 'Display only active variants.',
      initialStock: {
        title: 'Starting Stock',
        short: 'Start',
        description: 'Stock count at beginning of period.',
      },
      incomingStock: {
        title: 'Incoming Stock',
        short: 'In',
        description: 'General incoming stock during period.',
      },
      outgoingStock: {
        title: 'Outgoing Stock',
        short: 'Out',
        description: 'General outgoing stock during period.',
      },
      lostStock: {
        title: 'Shrinkage',
        short: 'Def.',
        description: 'Lost or damaged goods.',
      },
      soldDelivered: {
        title: 'Sold and Delivered',
        short: 'Del.',
        description: 'Sold and delivered in selected period.',
      },
      sentIncomplete: {
        title: 'Incomplete/Other',
        short: 'Inc.',
        description: 'Unclear product status, currently under investigation',
      },
      refundDelivered: { title: 'Returns', short: 'Retu.', description: 'Returned in selected period.' },
      clearingStock: {
        title: 'Clearing Amount',
        short: 'Clear.',
        description: 'Sold and delivered in selected period minus returned in this time.',
      },
      closingStock: {
        title: 'Ending Stock',
        short: 'End',
        description: 'Stock at the End of Period.',
      },
      sold: {
        title: 'Sold Regardless Delivery Status',
        short: 'Sold.',
        description: 'Items sold in the selected period, not regarding the delivery status.',
      },
      refund: {
        title: 'Refunded in Period',
        short: 'Refu.',
        description: '',
      },
      soldUnfulfilled: {
        title: 'Not fulfilled orders',
        short: 'Ord.',
        description: 'All items sold but not yet delivered, independent from time of purchase.',
      },
      reserved: {
        title: 'Reserved',
        short: 'Res.',
        description: 'Products reserved in unpayed transactions or carts.',
      },
      availableStock: {
        title: 'Available Items',
        short: 'Ava.',
        description: 'Products available at end of period.',
      },
      netSold: {
        title: 'Net Sold',
        short: 'Net Sold',
        description: 'Sold, minus Refund',
      },
    },
    shipping: 'Shipping',
    shippingRequirements: {
      _requirements: {
        over18: {
          title: '18+',
          description: "The recipient's age will be verified upon delivery. Deliveries only to persons aged 18+.",
        },
      },
      none: 'No shipping requirements',
    },
    shippingTags: {
      _tags: {
        warenpost: {
          prohibitedTitle: 'No Warenpost',
          prohibitedDescription: 'This product is to big or to heavy to be sent with Warenpost.',
        },
      },
    },
    musicCharts: {
      _name: 'Music Charts',
      recordLabel: 'Record label',
      recordDistributor: 'Record distributor',
      recordReleasedAt: 'Record released at',
      recordType: 'Record type',
      editMusicCharts: 'Edit music charts data',
      uploadOverrides: 'Upload music charts overrides',
      downloadChartOverrides: 'Download current music charts overrides',
    },
    bookCharts: {
      _name: 'Book Charts',
      gtin: 'GTIN / ISBN',
      productGroup: 'Product group',
      description1: 'Description 1',
      description2: 'Description 2',
      releasedAt: 'Released at',
      edit: {
        title: 'Edit Book Charts Data',
      },

      descriptions: {
        productGroup: 'Name of the product group, e.g. “Book”, “Games”, etc.',
        gtin: 'EAN/ISBN numeric without spaces or hyphens',
        description1: 'Title of the book, game, etc.',
        description2: 'Author of the book or performer, type or system for electronic games',
        releasedAt: 'In most cases congruent with the release date of the product',
      },
    },
    productVariant: {
      _name: 'Product Variant',
      _plural: 'Product Variants',
      activeFrom: 'Active From',
      activeUntil: 'Active Until',
      ean: 'EAN',
      eanInvalid: 'invalid EAN or UPC',
      eanValid: 'valid EAN code',
      upcValid: 'valid UPC code',
      pricingError: 'You need to add product variants to add prices',
      sku: 'Sku',
      private: "Don't show in shop",
      resaleAllowed: 're:sale allowed',
    },
    warehouseLocation: {
      _name: 'Warehouse Location',
      _plural: 'Warehouse Locations',
    },
    reference: {
      _name: 'Reference',
      _plural: 'References',
      merchCreditorReference: 'Creditor Reference (merch)',
      eventCreditorReference: 'Creditor Reference (event)',
    },
    refund: {
      _name: 'Refund',
      _plural: 'Refunds',
      refundItems: 'Refund Items',
      refundAndRelease: 'Refund and release',
      refundAndDestroy: 'Refund and destroy',
      refundAndBlock: 'Refund and block',
      reIssueTickets: 'Re-issue Tickets',
      refundModal: {
        refundOptionsTickets: (tickets: number) => `Refund options ${pluralize(tickets, 'Ticket')}`,
        refundOptionsMerch: 'Refund option merch',
        mixedWarning: (tickets: number) =>
          `You’re refunding a mixed selection of merch and ${pluralize(tickets, 'ticket')}.`,
        markDeliveryAsSent: 'Book incoming delivery note instantly',
      },
    },
    role: {
      editor: 'Editor',
      observer: 'Observer',
      owner: 'Owner',
      EDITOR: 'Editor',
      OBSERVER: 'Observer',
      OWNER: 'Owner',
    },
    sale: {
      _name: 'Sale',
      _plural: 'Sales',
      requestSale: 'Request sale',
      saleLaunch: 'Sale launch',
      salesStats: 'Sales Stats',
      startSale: 'Start sale',
      empty: 'No sales data yet',
      failedToLoad: 'Failed to Load Sales Data',
      returned: (tickets: number) => `${tickets} Tickets were refunded`,
      salesStatistics: 'Sales statistics',
      salesByPricingCategory: 'Tickets by pricing category',
      summary: 'Summary',
      lessThanZeroEuro: 'Free Tickets',
      moreThanZeroEuro: 'Regular Tickets',
      noSalesInPeriod: 'No sales in this period',
      totalSales: 'Total Sales',
    },
    shop: {
      _name: 'Shop',
      _plural: 'Shops',
      allShops: 'All Shops',
      defaultLocale: 'Default Locale',
      domain: 'Domain',
      logo: 'Logo',
      meta: 'Meta',
      parent: 'Parent',
      paymentBlacklist: 'Payment Blacklist',
      shopIndependent: 'Shop Independent',
      siteMap: 'Sitemap',
      waitingQueue: 'Waiting Queue',
      subtitleInShop: 'Show Subtitle In Shop',
      orderMergingAllowed: 'Order Merging allowed',
      shopsCategories: 'Shops / Categories',
      shopsAndCategories: 'Shops & Categories',
      shopsAndCategoriesModal: 'Shop & Categories Association',
      slug: 'Slug',
      slugFormat: 'Should only contain numbers or letters separated by a dash. Example: cool-shop',
      freeShippingThreshold: 'Minimum amount to get free shipping',
      customEmailSender: 'Customize Emails',
      customEmailSenderFields: {
        email: 'Email address',
        displayName: 'Display name',
        headerImage: 'Header Image',
        bodyBackgroundColor: 'Body Background Color',
        bodyForegroundColor: 'Body Foreground Color (e.g. text)',
        buttonBackgroundColor: 'Button Background Color',
        buttonForegroundColor: 'Button Foreground Color (e.g. text)',
        footerBackgroundColor: 'Footer Background Color',
        footerForegroundColor: 'Footer Foreground Color (e.g. text)',
        closingNotes: 'Closing Notes',
        emailBackgroundColor: 'Email Background Color',
        separatorLineColor: 'Separator Line Color',
      },
      customSenderAddress: 'Custom Sender Address',
      useCustomSenderAddress: 'Use Custom Sender Address?',
      customSenderAddressFields: {
        name1: 'Name 1',
        name2: 'Name 2',
        name3: 'Name 3',
        address: 'Street and house number',
        address2: 'Extra address 1',
        address3: 'Extra address 2',
        postCode: 'Post code',
        city: 'City',
        countryCode: 'Country code',
        contact: 'Contact',
        email: 'Email address',
        phone: 'Phone number',
      },
    },
    status: {
      _name: 'Status',
      active: 'Active',
      activeFrom: 'Active From',
      activeNow: 'Active Now',
      archived: 'Archived',
      available: 'Available',
      booked: 'Booked',
      blocked: 'Blocked',
      bookedCount: 'Booked',
      canceled: 'Canceled',
      cart: 'Cart',
      done: 'Done',
      draft: 'Draft',
      failed: 'Failed',
      processing: 'Processing',
      expressProcessing: 'Express Checkout',
      fulfillment: 'Fulfillment',
      incomplete: 'Incomplete',
      inReview: 'In Review',
      inactive: 'Inactive',
      local: 'Local issued',
      onSale: 'On Sale',
      paid: 'Paid',
      paymentPending: 'Payment Pending',
      pending: 'Pending',
      pendingFulfillment: 'Pending Fulfillment',
      pendingPayment: 'Payment Pending',
      permanent: 'permanently blocked',
      private: 'Private',
      problem: 'Problem',
      published: 'Published',
      reserved: 'Reserved',
      refundPending: 'Refund Pending',
      scheduled: 'Scheduled',
      sent: 'Sent',
      sold: 'Sold',
      stopped: 'Sale Stopped',
      systemFree: '+ free / companion tickets',
      systemSold: 'Sold (System)',
      unpublished: 'Unpublished',
      waiting: 'Waiting',
    },
    ticket: {
      _name: 'Ticket',
      _plural: 'Tickets',
      amount: (count: number) => (count === 1 ? `${count} Ticket` : `${count} Tickets`),
      additionalNotes: 'Additional Notes',
      hardticket: 'Hardticket',
      head: 'Head',
      logoTags: 'Tags',
      numberOfTickets: 'Number of tickets',
      onlineticket: 'Online',
      presenterLogo: 'Presenter logos',
      ticketPreview: 'Preview Ticket',
      ticketPreviewFullPdf: 'Download full PDF',
      someExplanation: 'Some explanation text',
      ticketImage: 'Ticket Image',
      ticketPersonalization: 'Ticket personalization',
      ticketTypes: 'Ticket Types',
      ticketing: 'Ticketing',
      ticketline: 'Ticketline',
      updateTicketInfo: 'Edit Ticket Details',
      updateGeneralInformation: 'Edit General Information',
      soldTicketsDist: 'Sold Tickets Distribution',
      noTicketsSold: 'No tickets sold yet',
      personalIdentifierDisclaimer: 'Personal Identifier Disclaimer',
      forcePrintPersonalization: 'Force print personalization on ticket',
      forcePrintPersonalizationDescription:
        'Any existing personalization will be printed on the ticket, even if personalization is turned off for the event.',
      updatePersonalIdentifier: 'Update personal identifier',
      personalIdentifier: 'Personal identifier',
      personalIdentifierDescription: 'Name of the attendee as shown on ID',
      ticketLayout: 'Ticket Layout',
      onlineTicketLayout: 'Online Ticket Layout',
      localTicketLayout: 'Local Ticket Layout',
      headerImage: 'Header Image',
      pricingVariant: 'Pricing Variant',
      ticketNumber: 'Ticket number',
      orderHistory: 'Order history',
      cancelTickets: 'Cancel Tickets',
      refundOptions: 'Refund Options',
      cancelAndRelease: 'Cancel and Release',
      cancelAndBlock: 'Cancel and Block',
      printedTickets: 'Printed Tickets',
      localPricing: 'Local Pricing',
      systemPricing: 'System Pricing',
      design: 'Design',
      selectFreeSeats: 'Select free seats or a block to generate free tickets.',
      personalized: 'Personalized',
      notPersonalized: 'Not personalized',
      checkedIn: 'Checked in',
      notCheckedIn: 'Not checked in',
      checkInOpens: 'Check-In opens',
      checkInCloses: 'Re-Check-In closes',
      checkInClosesExplanation:
        'From this time on check-in data can not be overwritten anymore. The initial check-in is still possible.',
      selectedTickets: 'Selected tickets',
      issueTickets: 'Issue Tickets',
      overview: 'Overview',
      seat: 'Seat',
      codes: 'Codes',
      used: 'Used',
      unused: 'Unused',
      ticketScanner: 'Ticket Scanner',
      enableTicketScanner: 'Enable Ticket Scanner',
      eventCodes: 'Event Codes',
      resale: 're:sale',
      q: 'Q',
      search: 'Search for Recipient, Email, Tags',
      recipient: 'Recipient',
      pricingCategory: 'Pricing Cat.',
      emailDeliveryDates: 'Email Delivery Dates',
      notAvailable: 'N/A',
      commentEmailText: 'Comment / Email text',
      noTicketsIssued: 'No tickets issued',
      pricingCategories: 'Pricing Category',
      productVariant: 'Product Variant',
      venueSection: 'Venue Section',
      preview: 'Preview',
      hardProductVariant: (description: string) => `${description} Hard`,
      onlineProductVariant: (description: string) => `${description} Online`,
      passbook: 'Wallet Ticket',
      ticketPassword: 'Ticket Password',
      resaleOffer: {
        _name: 're:sale Offer',
        onResale: 'on re:sale',
        repurchasedInOrder: 're-purchased in Order',
        public: 'public',
        private: 'private',
      },
      resoldByOffer: {
        originalOrder: 'Original Order',
      },
      rerender: {
        title: 'Rerender Tickets',
        description: 'Rerendering tickets will happen asynchronously and can take a few minutes after requesting it.',
        online: 'Rerender Online Tickets',
        hard: 'Rerender Hard Tickets',
      },
    },
    resendTickets: {
      confirmResendingEmail: 'Confirm the resending of the tickets',
      title: 'Are you sure you want to resend the tickets to the recipient?',
      subtitle:
        'This action will generate a new email containing the issued tickets and send it to the following details:',
      name: 'Name',
      recipientEmailAddress: "Recipient's Email Address:",
      noOfTickets: 'No. of Tickets:',
      resend: 'Resend',
      success: 'Successfully resent tickets',
    },
    tour: {
      _name: 'Tour',
      _plural: 'Tours',
      comment: 'Comment',
      displayTitle: 'Display Title',
      displayTitleMessage: 'e.g. Bands, Support-Bands makes it better searchable',
      excludeFromFeeds: 'Exclude from Feeds (Facebook & Google)',
      internalTitle: 'Internal Title',
      showSubtitle: 'Show subtitle in shop',
      sideImage: 'Side Image',
      sortOrder: 'Sort order',
      sortOrderHomepage: 'Sort order homepage',
      sortOrderTicketBottom: 'Sort order tickets / bottom',
      sortOrderTicketTopLeft: 'Sort order tickets / top left',
      sortOrderTicketTopRight: 'Sort order tickets / top right',
      tableInternalTitle: 'Internal Title (Title)',
      noTours: 'No tours were found.',
      eventsInThisTour: 'Events in this tour',
      noEvents: 'No events in this tour',
      meta: 'Meta',
    },
    tourArtists: {
      _name: 'Associated Artists',
      _plural: 'Artists',
    },
    comment: {
      _name: 'Comment',
      _plural: 'Comments',
    },
    user: {
      _name: 'User',
      _plural: 'Users',
      admin: 'Admin',
      contactDetails: 'Contact Details',
      customer: 'Customer',
      email: 'Email',
      firstName: 'First Name',
      lastName: 'Last Name',
      name: 'Name',
      phone: 'Phone',
    },
    variant: {
      _name: 'Variant',
      _plural: 'Variants',
    },
    venue: {
      _name: 'Venue',
      _plural: 'Venues',
      allVenues: 'All Venues',
      eventsForVenue: 'Events for Venue',
      location: 'Location',
      venueName: 'Venue Name',
      noEvents: 'No events for this venue',
    },
    venueLayout: {
      _name: 'Venue Layout',
      _plural: 'Venue Layouts',
      contingent: 'Contingents',
      archived: 'Archived',
      importStatus: {
        pending: 'Importing...',
        done: null,
        error: 'Import failed',
      },
      noContingent: 'No Contingent set for this Section',
      quickNav: 'Quick Nav',
      seatmaps: 'Seatmaps',
      seats: 'Seats',
      seatsCsv: 'Seats CSV',
      seatsAndAllocation: 'Seats & Allocation List',
      seatsAndBlocks: 'Seats and Blocks',
      section: 'Section',
      selected: 'Selected',
      name: 'Name',
      capacity: 'Capacity',
      capacitySetBySeatMap: 'Capacity is set by Seat map.',
      selectedSeatsAndBlocks: 'Selected Seats and Block',
      numberOfSelectedSeats: 'Selected Seats total',
      currentPricingCategory: 'Current Pricing Category:',
      selectCategory: 'Select new Pricing Category',
      multiplePricingCategories: 'Multiple pricing categories',
      addRowToSelection: 'Add row to selection',
      addSectionToSelection: 'Add section to selection',
      pleaseSelectPricingCategory: 'Please select a pricing category',
      createEventWithThisLayout: 'Create Event with this Layout',
    },
    server: {
      configuration: 'Server configuration',
      status: 'Server status',
      scalingSchedule: 'scheduled scalings',
      noSchedules: 'There are no scheduled scalings',
      serverMode: 'Server mode',
      activeOn: 'Active on',
      actions: 'actions',
      createSchedule: 'Schedule scaling',
      deleteSchedule: 'Delete scheduled scaling',
      confirmDeleteSchedule: 'Are you sure you want to delete this scheduled scaling?',
    },
    contingent: {
      capacityAndAvailability: 'Capacity and Availability',
      capacity: 'Capacity',
      issued: 'Issued',
      reserved: 'Reserved',
      shoppingCart: 'Shopping Cart',
      availableTickets: 'Available Tickets',
      blocks: 'Blocks',
      options: 'Options',
      noBlocksOrOptionsYet: 'No blocks or options created yet',
      adjustCapacity: 'Adjust capacity',
      freeTickets: 'Tickets',
      generateFreeTickets: 'Generate tickets',
      hereYouCanGenerateFreeTickets:
        'Here you can generate free tickets for guests and other occasions like box office.',
      newTickets: 'New Tickets',
      newTicketsWillBeCreated:
        'New tickets will be created if you increase the capacity. What do you want to do with them?',
      makeAvailable: 'Make them available',
      blockThem: 'Block them',
      pool: 'Pool',
      editContingent: 'Edit Contingent',
      jumpToArea: 'Jump to area',
      createPools: 'Create Blocks / Pools',
      createPoolsExplanation: 'Here you can block seats and create pools, e.g. for guest lists or unused capacities.',
      createPoolButton: 'Block / Create Pool',
      blockReason: 'Block reason',
      noneCreated: 'None created',
      createBlocks: 'Create blocks / options',
    },
    automation: {
      startNow: 'Start now',
      automation: 'Automation',
      stopNow: 'Stop now',
      startCampaignAt: 'Start campaign at',
      stopCampaignAt: 'Stop campaign at',
      setAutomation: 'Set automation',
      editAutomation: 'Edit automation',
      operation: {
        start: 'Start',
        stop: 'Stop',
      },
    },
  },
  page: {
    sidebar: {
      fulfillment: 'Fulfillment',
      settings: 'Settings',
      backPayments: 'Refunds',
      build: 'build',
      lastUpdatedAt: 'Last updated at',
      contentAndCampaigns: 'Content & Campaigns',
      voucherCampaigns: 'Voucher Campaigns',
    },
    dashboard: {
      editTeam: 'Edit Team',
      preview:
        'This software is an early preview. The shown data comes without any guarantee of correctness. All current functionality is subject to future changes.\nCurrently you can: \n- update your personal account (e.g. your contact data and your avatar) \n- edit your team (e.g. upload a logo or invite other team members) \n- have a look at your sales report\nIf you find an error or have other helpful insights please send an email with the subject “Admin Bug Report” to info@krasserstoff.com or just give us a call at 030-632222-422.\n',
      title: 'Dashboard',
      welcome: "Welcome - You're working with alpha software, therefore we have some notes",
      noDataToShow: 'Nothing to show here',
      resetTimeframe: 'Reset timeframe',
    },
    fulfillmentDetails: {
      bookDeliveryNote: 'book delivery note',
      deliverySuccess: 'Delivery was successfully booked!',
      orderSent: 'Order has been sent',
      packedProducts: 'Packed products',
      printShippingLabel: 'print shipping label',
      printTicket: 'Print Ticket',
      printDHLLabel: 'Print DHL Label',
      printTickets: 'Print Tickets',
      productName: 'Product name',
      productsToPack: 'Products to pack',
      scanLabel: 'Scan DHL label or type label number',
      scanProductOrSku: 'Scan product, type SKU or Validation Code',
      validationCode: 'VALIDATION CODE',
      noProductsToFulfill: 'No products to fulfill',
      problematicOrderTitle: 'Problematic order',
      problematicOrderText: 'This order has the status "Problem" and probably shouldn\'t be fulfilled yet.',
      fulfillAnyway: 'Start fulfillment anyway',
      confirmBooking: 'Confirm Booking',
      confirmBookingDescription: (code: string) => `Are you sure you want to continue booking with the code ${code}?`,
    },
    login: {
      checkMail: 'Please check your email.',
      confirmPassword: 'Confirm Password',
      forgotPassword: 'Forgot password',
      loginFailed: 'Failed to Login, Try loging in manually',
      loginFormTitle: 'Sign in to continue',
      newPassword: 'Enter New Password',
      password: 'Password',
      rememberLogin: 'Remember login',
      resetPassword: 'Reset Password',
      title: 'Login',
      createAccount: 'Create Account',
      hasAccount: 'If you already have an account with KS, you can use that to login.',
    },
    turnover: {
      menuTitle: 'Turnover',
      title: 'Turnover',
      summary: 'Summary',
      loadMore: 'Load more',
      date: 'Date',
      product: 'Name',
      sku: 'Event',
      pkVariant: 'PC / Variant',
      count: 'Count',
      brutPrice: 'Price (brut)',
      brutTotal: 'Revenue (brut)',
      vatRate: 'VAT Rate',
      vat: 'VAT Amount',
      downloadAsPdf: 'Download as PDF',
    },
    signup: {
      title: 'Signup',
      signupFormTitle: 'Sign up to continue',
      userHasInvitedYou: ({ invitedBy, company, role }: Partial<Membership>) => {
        const user = userNameAndOrEmail(invitedBy?.name, invitedBy?.email);
        const translatedRole = role ? I18n.translate.model.role[role] : '';
        return `${user} invited you to join <strong>${company?.name}</strong> as an ${translatedRole}.`;
      },
      pleaseSignUp: 'Please signup to accept the invite.',
      password: 'Set password',
      confirmPassword: 'Confirm password',
      tos: 'I acknowledge that I have read and understood the Terms and Conditions and Privacy Policy (as may be updated from time to time), and hereby agree to be bound by such terms.',
      alreadyHaveAccount: 'Already have an account?',
    },
    queue: {
      active: 'shopping',
      activeFor: (names: string) => `Active for ${names}.`,
      free: 'free slots',
      title: 'Waiting Queue',
      waiting: 'waiting',
      waitingQueueStats: 'Waiting Queue Stats',
      sessionInactivity: 'Session Inactivity',
      activeSessions: 'Active Sessions',
      sessions: 'Sessions',
      waitingTime: 'Waiting Time',
      maximumSpotsCount: 'Checkout Spots Count',
      maximumCatalogSpotsCount: 'Catalog Spots Count',
      ordersCreatedPerMinute: 'Orders Created per Minute',
      ordersPerMinute: 'Orders per Minute',
      paymentsPerMinute: 'Payments per Minute',
      checkoutQueue: 'Checkout Queue',
      catalogQueue: 'Catalog Queue',
      topIps: 'Top 10 IPs by Orders Created in the Last 120 Minutes',
      topPaymentFingerprints: 'Top Payment Fingerprints by Number of Orders in the Last 30 Minutes',
    },
    settings: {
      title: 'Settings',
      ordersAutoCancel: 'Orders Auto Cancellation',
    },
    styleguide: {
      title: 'Styleguide',
    },
    salesReport: {
      downloadButton: 'Sales Report',
      title: 'Download Sales Report',
      explanation: 'Select the information from the checkboxes that should be in the sales report.',
      priceParts: 'Price Parts',
      prices: 'Prices',
      generatePdf: 'Generate PDF',
      inclFreeLocalTickets: 'incl. Local Tickets = 0.00 EUR',
      inclPaidLocalTickets: 'incl. Local Tickets > 0.00 EUR',
      basePrice: 'Base Price',
      fee: 'Presale Fee & System Fee',
    },
    ticketScanner: {
      valid: 'Valid ticket',
      invalid: 'Invalid ticket',
      checked: 'Already checked in',
      checkIn: 'Check in',
      gate: 'Gate',
      scanTicket: 'Scan',
      enterCode: 'Enter code',
      startScanning: 'Scan',
      stopScanning: 'Stop scanning',
      checkout: 'Check out now?',
      enterTicketNumber: 'Enter ticket number',
      submit: 'Submit',
      activityLog: 'Activities',
      noActivity: 'No activity yet',
      scan: 'Scan',
      fewSecondsAgo: 'a few seconds ago',
      section: 'Section',
      row: 'Row',
      seat: 'Seat',
      manualEntry: 'Manual entry',
      checkedOut: 'Checked out',
      scanCodeToGetStarted: 'Please scan the Event-Code to get started.',
      invalidEventCode: 'This does not seem to be an Event-Code.',
      contactUs: 'Do you need help? Contact us',
      loadingText: 'Currently retrieving ticket codes. Please wait.',
      checkOutWarning: 'Once checked out, the ticket is valid again and can be used again',
      checkOutWarningDismiss: 'Do not show again',
      checkOutWarningAccept: 'Got it!',
    },
    event: {
      salesByGeography: 'Sales by Geography',
      salesByGeographyTooltip:
        'The geography is determined on the basis of the postal code of the ordering person. In countries for which no detailed postal code information is stored, the sum of all orders of the country is summarized in a black square. The distribution refers to the ordering persons, not to the ticket users. It therefore only allows limited conclusions to be drawn about visitors.',
      salesByGender: 'Sales By Gender',
      salesByGenderTooltip:
        'The gender is determined on the basis of the first name of the person placing the order. This method saves customers the question of gender assignment and allows a relatively good and abuse-proof determination of the lived assignment. Please note: The distribution refers to the ordering persons, not to the ticket users. It therefore only allows limited conclusions to be drawn about visitors.',
      ticket: {
        print_section_ticket_overlay: {
          tags_description: 'e.g. Artist’s crew/family/friends',
          send_via_email: 'Send via email',
          email_text: 'Email Text',
        },
      },
      details: {
        ticketDelay: {
          cardTitle: 'Delayed Online Tickets',
          noAutomation: 'No automation configured',
          ticketsWillBeSentAt: 'Tickets will be sent at',
          sendTicketsAt: 'Send tickets at',
          removeAutomationHint:
            'Tip: Set the date to the past to make sure all tickets get sent out as soon as possible. Afterwards, the automation will be removed automatically.',
        },
      },
      design: {
        passbook: {
          title: 'Wallet Ticket',
          enabled: 'Generate Wallet Tickets for Online Tickets',
          enabledTrue: 'Wallet Tickets are generated for Online Tickets',
          enabledFalse: 'Wallet Tickets are not generated. Existing Wallet Tickets can still be downloaded.',
          preview: 'Download Preview',
          edit: {
            enabled: 'Generate Wallet Tickets for Online Tickets',
            title: 'Edit Wallet Ticket Settings',
          },
        },
        publicTransport: {
          title: 'Public Transport',
          description: 'Description',
          company: 'Company',
          edit: {
            enabled: 'Include public transport information',
            title: 'Edit Public Transport',
            description: 'Description',
            company: 'Company',
          },
        },
        ticketLayouts: {
          title: 'Ticket Layouts',
          edit: {
            title: {
              local: 'Edit Local Ticket Layout',
              hard: 'Edit Ticket Layout',
              online: 'Edit Online Ticket Layout',
            },
            customHtml: 'Custom HTML',
            previewError: 'Error rendering template. Please check your HTML and Liquid syntax.',
          },
        },
      },
      utmTrackingSourcesCard: {
        noUtmData: 'No UTM Data',
      },
      utmTrackingCampaignsCard: {
        noUtmData: 'No UTM Data',
      },
      utmTrackingMediaCard: {
        noUtmData: 'No UTM Data',
      },
      create: {
        venueLayout: 'Venue Layout',
        pricingCategory: {
          _name: 'Pricing Category',
          _plural: 'Pricing Categories',
        },
        capacity: 'Capacity',
        addEvent: 'Add Event',
        venue: 'Venue',
        venues: 'Venues',
        blockAllSeats: 'Create event with all seats blocked?',
        yes: 'Yes',
        no: 'No',
        event: 'Event',
        ticketline1: 'Ticketline 1',
        ticketline2: 'Ticketline 2',
        ticketline3: 'Ticketline 3',
        ticketlineHeadline: 'Ticketline headline',
        ticketlineFooter: 'Ticketline footer',
        tourOperator: 'Tour Operator',
        localOperator: 'Local Operator',
        description: 'Description',
        excludeFromFeeds: 'Exclude from Feeds (Facebook & Google)',
        datesAndTimes: 'dates and times',
        startDate: 'Start Date',
        multiday: 'Multiday',
        endDate: 'End Date',
        activeFrom: 'Active From',
        doorTime: 'Door Time',
        stageTime: 'Stage Time',
        personalization: 'Personalization',
        formatHHMM: 'Format: HH:MM',
        noPersonalizationRequired: 'No personalization',
        groupPersonalizationRequired: 'Group personalization (same name for all tickets)',
        ticketPersonalizationRequired: 'Ticket personalization (one name per ticket)',
        contactTracingRequired: 'Contact Tracing',
        checkInRequired: 'Check-In',
        legacyId: 'Legacy ID',
        shopSettings: 'shop settings',
        publishDate: 'Publish Date',
      },
    },
    voucherCampaigns: {
      voucherCampaigns: 'Voucher Campaigns',
      title: 'Title',
      createdOn: 'created on',
      voucherType: 'Voucher Type',
      product: 'Product discount',
      shipping: 'Shipping discount',
      codes: 'Codes',
      usageCodes: 'Usage / Codes',
      status: 'Status',
      automations: 'Automations',
      start: 'Start',
      stop: 'Stop',
      campaignDetails: 'Campaign Details',
      form: {
        create: 'Create Voucher Campaign',
        edit: 'Edit Voucher Campaign',
        shop: 'Associated Shop',
        title: 'Title',
        internalTitle: 'Internal Title',
        target: 'Voucher type and rate',
        targetDescription: 'Select the type of vouchers and how are the costs going to be split.',
        targetProduct: 'On Merchandise / Events',
        targetShipping: 'On Shipping',
        amount: 'Amount',
        type: 'Type/unit',
        discountTypePercentage: 'Percentage',
        discountTypeFixed: 'Fixed',
        amountDescription:
          'Discounts can be absolute or percentage based. Enter amount and selet type. Eg: 20% off on merchandise',
        costSplit: 'Cost split',
        costSplitDescription: (value?: string) =>
          `The Artist will pay <strong>${value}</strong> for the voucher applied.`,
        voucherUsage: 'Voucher Usage',
        voucherUsageDescription: 'Select whether the vouchers of this campaign can be redeemed once or multiple times.',
        multiple: 'Multiple',
        single: 'Single',
        amountPercentageError: 'Please enter a valid amount between 0 and 100',
      },
    },
    voucherCampaign: {
      voucherCampaigns: 'Voucher Campaigns',
      vouchers: 'Vouchers',
      campaignDetails: 'Campaign Details',
      shop: 'Shop',
      voucherTypeAndRate: {
        title: 'Voucher Type and Rate',
        use: {
          multiple: 'Multiple use',
          single: 'Single use',
        },
        type: {
          absolute: 'Absolute-based',
          percentage: 'Percentage-based',
        },
      },
      codeUsage: 'Code Usage',
      contractDiscountShare: 'Contract Discount Share',
      delete: {
        title: 'Delete Voucher Campaign',
        description: 'Are you sure you want to delete this voucher campaign?',
        confirm: 'Delete',
      },
      voucherCodes: {
        voucherCodes: 'Voucher Codes',
        code: 'Code',
        totalUsed: 'Total Used',
        noVoucherCodes: 'No voucher codes',
        singleResultSelected: '1 search result selected',
        manyResultsSelected: (count: number) => `${count} search results selected`,
        allSelected: 'All search results selected',
        selectAll: 'Select all',
        downloadCodes: 'Download Codes',
        create: {
          voucherCode: 'Voucher Code',
          enterCode: 'Enter Code',
          generateCodes: 'Generate Codes',
          codeDescription: "E.g. 'MOTHERSDAY24'",
          amountOfCodes: 'Amount of Codes',
        },
        import: {
          importCsv: 'Import CSV',
          attachment: 'Attachment',
          description: 'Please format your file with just one column and one code per line.',
          import: 'Import',
        },
        update: {
          editVoucherCode: 'Edit Voucher Code',
          enterCode: 'Enter Code',
        },
        delete: {
          single: {
            title: 'Delete Voucher Code',
            description: 'Are you sure you want to delete this voucher code?',
          },
          multiple: {
            title: 'Delete Voucher Codes',
            description: 'Are you sure you want to delete these voucher codes?',
          },
          all: {
            title: 'Delete all Voucher Codes',
            description: 'Are you sure you want to delete all voucher codes for this campaign?',
          },
          confirm: 'Delete',
        },
      },
      discountedProductsAndEvents: {
        title: 'Discounted Products and Events',
        merchandise: 'Merchandise',
        events: 'Events',
        singleResultSelected: '1 search result selected',
        manyResultsSelected: (count: number) => `${count} search results selected`,
        allSelected: 'All search results selected',
        selectAll: 'Select all search results',
        switcherType: 'Type',
        create: {
          productsAndEventsAssociation: 'Products and Events Association',
          merchandise: 'Merchandise',
          events: 'Events',
          selectType: 'Select Type',
          keepModalOpen: 'Keep Modal Open',
          cancel: 'Cancel',
          add: 'Add',
        },
        delete: {
          single: 'You are about to remove a product. Do you want to proceed?',
          multiple: (items: number) => `You are about to remove ${items} products. Do you want to proceed?`,
          all: 'You are about to remove all products. Do you want to proceed?',
          confirm: 'Delete',
        },
        table: {
          emptyProducts: (shop: string) =>
            `All products from ${shop} shop are being discounted. To change this selection, please add specific products.`,
          sku: 'SKU',
          artist: 'Artist/Brand',
          categories: 'Categories',
          productType: 'Type',
          motive: 'Motive',
          color: 'Color',
          price: 'Price',
          availability: 'Availability',
          noProductVariants: 'No product variants available.',
          description: 'Description',
          emptyEvents: 'Please add events to get started.',
          event: 'Event',
          venue: 'Venue',
          date: 'Date',
          noEventsAvailable: 'No events available.',
          pricingCategory: 'Pricing Category',
          variant: 'Variant',
          priceWithDesc: 'Price (€ net/gross)',
          noSearchResultsFound: 'No search results found.',
        },
      },
    },
  },
  success: {
    addedItems: (item: string) => `Succesfully added ${item}`,
    deletedItems: (item: string) => `Succesfully deleted ${item}`,
    refundedItems: (item: string) => `Succesfully refunded ${item}`,
  },
};
